import { useCallback, useRef, useState } from 'react';

import { Audit, useListProjectActivityFeedLazyQuery } from '@graphql/generated';
import { useResultsTruncated } from '@hooks/useResultsTruncated';
import { FETCH_TIMEOUT_MS } from '@utils/fetchWithTimeout';

type ProjectActivityVariables = {
  projectId: string;
  term: string;
  userIds?: string[];
  startDate?: string;
  endDate?: string;
  first: number;
  /** Relay cursor to continue from; omitted for page 0. */
  after?: string;
};

/**
 * Project Activity tab fetch via listActivitiesByProject (TA-11040).
 *
 * The tab used to page through globalActivitySearch(projectIds:), which the
 * backend paywalls to 3 entries for a viewer with no active team
 * subscription. listActivitiesByProject carries the same term/user/date
 * filters without the paywall, but pages by Relay cursor instead of a raw
 * from/size offset, so `afterRef` replaces useGlobalActivityFetch's
 * `fromRef` and "has more" comes from pageInfo rather than a total count.
 */
export function useProjectActivityFetch() {
  const [activityList, setActivityList] = useState<Audit[]>([]);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const afterRef = useRef<string | undefined>(undefined);
  const fetchGenRef = useRef(0);

  const { isTruncated, setTruncatedFromError, resetTruncated } =
    useResultsTruncated();
  const setTruncatedRef = useRef(setTruncatedFromError);
  setTruncatedRef.current = setTruncatedFromError;
  const resetTruncatedRef = useRef(resetTruncated);
  resetTruncatedRef.current = resetTruncated;

  // no-cache: the listActivitiesByProject type policy keys only on
  // projectId, so cached pages from different term/filter combinations
  // would be merged into one list.
  // The operation is named listProjectActivityFeed, not after the field:
  // ~20 mutations refetch 'listActivitiesByProject' by name, and Apollo
  // re-runs a no-cache lazy query matched that way with its last variables
  // (often a stale `after` cursor) for a result this hook never reads.
  const [listActivitiesByProject] = useListProjectActivityFeedLazyQuery({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const doFetch = useCallback(
    async (variables: ProjectActivityVariables) => {
      const isFirstPage = !variables.after;
      if (isFirstPage) fetchGenRef.current += 1;
      const myGen = fetchGenRef.current;

      if (isFirstPage) {
        setIsLoading(true);
        setIsLoadingMore(false);
        resetTruncatedRef.current();
      } else {
        setIsLoadingMore(true);
      }

      // Same watchdog as useGlobalActivityFetch (TA-11017): RetryLink can
      // hold a slow request for minutes, so release the loading state after
      // one attempt's worth of waiting.
      let settled = false;
      const watchdog = setTimeout(() => {
        if (settled || fetchGenRef.current !== myGen) return;
        if (isFirstPage) {
          setIsLoading(false);
          setRefreshing(false);
        } else {
          setIsLoadingMore(false);
        }
      }, FETCH_TIMEOUT_MS);

      try {
        const queryResult = await listActivitiesByProject({ variables });
        settled = true;
        if (fetchGenRef.current !== myGen) return;
        if (isFirstPage) setTruncatedRef.current(queryResult);
        const connection = queryResult.data?.listActivitiesByProject;
        if (connection) {
          const records = (connection.edges ?? [])
            .map((edge) => edge?.node)
            .filter(Boolean) as Audit[];
          if (isFirstPage) {
            setActivityList(records);
            setIsLoading(false);
            setRefreshing(false);
          } else {
            setActivityList((prev) => {
              const newItems = records.filter(
                (r) => !prev.some((existing) => existing.id === r.id)
              );
              return newItems.length > 0 ? [...prev, ...newItems] : prev;
            });
            setIsLoadingMore(false);
          }
          const { hasNextPage, endCursor } = connection.pageInfo;
          afterRef.current = endCursor ?? undefined;
          setCanLoadMore(hasNextPage && !!endCursor);
        } else {
          setIsLoading(false);
          setIsLoadingMore(false);
          setRefreshing(false);
        }
      } catch {
        settled = true;
        if (fetchGenRef.current !== myGen) return;
        setIsLoading(false);
        setIsLoadingMore(false);
        setRefreshing(false);
      } finally {
        clearTimeout(watchdog);
      }
    },
    [listActivitiesByProject]
  );

  // Drops any request still in flight. The screens clear the list as soon as
  // a search crosses the threshold but only fetch after the debounce, and a
  // response landing in that window would otherwise still match the current
  // generation and refill the cleared list with the previous term's results.
  const invalidatePending = useCallback(() => {
    fetchGenRef.current += 1;
  }, []);

  return {
    activityList,
    setActivityList,
    canLoadMore,
    setCanLoadMore,
    isLoading,
    setIsLoading,
    isLoadingMore,
    refreshing,
    setRefreshing,
    afterRef,
    isTruncated,
    doFetch,
    invalidatePending,
  };
}
